/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react'

import PropTypes from 'prop-types'

import styleNames from '@pareto-engineering/bem/exports'

import './styles.scss'

// Local Definitions

import {
  SocialMediaButton,
  Page,
  ThemeSelector,
  SVG,
} from '@pareto-engineering/design-system'

import { Link } from 'react-router-dom'

import {
  defaultPrivacyItems,
  defaultNavigationItems,
  defaultSocialItems,
  defaultOpsServiceItems,
  defaultUseCaseItems,
} from './defaultConfig'

const baseClassName = styleNames.base

const componentClassName = 'footer'

const logoData = {
  logoSrc:'/wbenc-logo.svg',
  target :'WBENC-Logo',
}

/**
 * This is the component description.
 */
const Footer = ({
  id,
  className:userClassName,
  style,
  backgroundColor,
  accentColor,
  title,
  useCaseItems,
  opsServiceItems,
  navigationItems,
  socialItems,
  privacyItems,
  spaced,
}) => (
  <Page.Section
    padded={false}
    id={id}
    className={[
      baseClassName,
      componentClassName,
      userClassName,
      `x-${accentColor}`,
      `y-${backgroundColor}`,
    ]
      .filter((e) => e)
      .join(' ')}
    style={style}
  >
    <div
      className={`wrapper grid span-grid ${spaced && styleNames.modifierSpaced}`}
    >
      <div className="title span-8 md-start-2 md-span-12">
        <p className="h2 remove-top-margin">{title}</p>
        <ThemeSelector className="hide-on-mobile" />
      </div>
      <div className="footer-nav md-start-2 md-span-3 span-4">
        <p className="remove-top-margin h4">
          Solutions
        </p>
        {useCaseItems?.map((item) => (
          <div className="link-wrapper" key={item.name}>
            {item.Component
              ? <item.Component />
              : <Link to={item.link}>{item.name}</Link>}
          </div>
        ))}
      </div>
      <div className="footer-nav md-start-5 md-span-3 span-4">
        <p className="remove-top-margin h4">
          Ops services
        </p>
        {opsServiceItems?.map((item) => (
          <div className="link-wrapper" key={item.name}>
            {item.Component
              ? <item.Component />
              : <Link to={item.link}>{item.name}</Link>}
          </div>
        ))}
      </div>
      <div className="footer-nav md-start-8 md-span-3 span-4">
        <p className="remove-top-margin h4">
          Resources
        </p>
        {navigationItems.map((item) => (
          <div className="link-wrapper" key={item.name}>
            {item.Component
              ? <item.Component />
              : <Link to={item.link}>{item.name}</Link>}
          </div>
        ))}
      </div>
      <div className="footer-nav md-start-11 md-span-3 span-4">
        <p className="remove-top-margin h4">
          Contact
        </p>
        <a
          href="mailto:team@pareto.ai"
          className="contact-info"
        >
          team@pareto.ai
        </a>
      </div>
      <ThemeSelector className="hide-on-desktop start-5 span-4" />
      <p className="logo s2 md-start-2 md-span-2 span-8">
        <span className="icon s2 c-x x-heading">z</span>
        <span className="h3">Pareto</span>
      </p>
      <div className="social-links md-start-11 md-span-3 span-8">
        {socialItems.map((item) => (<SocialMediaButton key={item} type={item} />))}
      </div>
      <div className="span-grid grid bottom-section md-start-2 md-span-12">
        <div className="md-start-2 md-span-12 span-8">
          <div className="copyright-info">
            <p className="remove-top-margin">
              &copy;
              {' '}
              {new Date().getFullYear()}
              {' '}
              Pareto All Rights Reserved.
            </p>
            <SVG className="md-start-12 md-span-3 span-8" sprite={logoData.logoSrc} target={logoData.target} width="100%" viewBox="0 0 89 89" />
          </div>
        </div>
        <div className="privacy-info span-8">
          {privacyItems.map((item) => (
            <div className="link-wrapper" key={item.name}>
              {item.Component
                ? <item.Component />
                : <Link to={item.link}>{item.name}</Link>}
            </div>
          ))}
        </div>
      </div>
    </div>
  </Page.Section>
)

Footer.propTypes = {
  /**
   * The HTML id for this element
   */
  id:PropTypes.string,

  /**
   * The HTML class names for this element
   */
  className:PropTypes.string,

  /**
   * The React-written, css properties for this element.
   */
  style:PropTypes.objectOf(PropTypes.string),

  /**
   * The text displayed at the top of the footer
   */
  title:PropTypes.string,

  /**
   * The internal navigation section
   */
  navigationItems:PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),

  /**
   * The links for the privacy policy, terms, cookies
   */
  privacyItems:PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),

  /**
   * The social media list to display
   */
  socialItems:PropTypes.arrayOf(PropTypes.string),

  /**
   * The background color of the footer
   */
  backgroundColor:PropTypes.string,

  /*
  The accent color of the footer
   */
  accentColor:PropTypes.string,

  /*
  Exclude padding from the bottom of the footer
   */
  spaced:PropTypes.bool,

  /**
   * The use cases section
   */
  useCaseItems:PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),

  /**
   * The ops services section
   */
  opsServiceItems:PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
}

Footer.defaultProps = {
  backgroundColor:'background-far',
  title          :'Premium data by top labelers to fine-tune AI/LLM models',
  navigationItems:defaultNavigationItems,
  privacyItems   :defaultPrivacyItems,
  socialItems    :defaultSocialItems,
  opsServiceItems:defaultOpsServiceItems,
  useCaseItems   :defaultUseCaseItems,
  spaced         :true,
}

export default Footer
